/**
 * Vendors API hooks — TanStack Query layer over /api/vendors.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../../lib/api.js';
import type { Vendor, Paginated } from '@shared/types';
import { vendorCreateInput, vendorUpdateInput } from '@shared/schemas/index.js';
import { z } from 'zod';

export type VendorCreateBody = z.input<typeof vendorCreateInput>;
export type VendorUpdateBody = z.input<typeof vendorUpdateInput>;

export const vendorKeys = {
  all: ['vendors'] as const,
  list: (params?: { search?: string; status?: string }) =>
    [...vendorKeys.all, 'list', params ?? {}] as const,
  detail: (id: string) => [...vendorKeys.all, 'detail', id] as const,
};

export function useVendors(params?: { search?: string; status?: string }) {
  return useQuery({
    queryKey: vendorKeys.list(params),
    queryFn: () =>
      api.get<Paginated<Vendor>>(`/api/vendors${qs({ ...params, limit: 1000 })}`),
  });
}

export function useVendor(id: string | undefined) {
  return useQuery({
    queryKey: vendorKeys.detail(id ?? ''),
    queryFn: () => api.get<Vendor>(`/api/vendors/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: VendorCreateBody) => api.post<Vendor>('/api/vendors', body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}

export function useUpdateVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: VendorUpdateBody }) =>
      api.put<Vendor>(`/api/vendors/${id}`, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(vendorKeys.detail(updated.id), updated);
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}

export function useDeleteVendor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ ok: boolean }>(`/api/vendors/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: vendorKeys.all });
    },
  });
}
